import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogicalSeparator } from './LogicalSeparator.tsx';
import { MoreTagItem } from './MoreTagItem.tsx';
import { TagItem } from './TagItem.tsx';

type TagGroup = {
  tags: string[];
};

type TagGroupDisplayProps = {
  group: TagGroup;
  groupIndex: number;
  hideOpeartors?: boolean;
};

export const TagGroupDisplay = ({ group, groupIndex, hideOpeartors = false }: TagGroupDisplayProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (stackRef.current && containerRef.current) {
      setIsOverflowing(stackRef.current.scrollWidth > containerRef.current.clientWidth);
    }
  }, []);

  return (
    <Stack
      direction="column"
      ref={containerRef}
      sx={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Typography variant="overline" sx={{ textTransform: 'uppercase' }}>
        {`${t('group')} ${groupIndex + 1} [${group.tags.length}]`}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        ref={stackRef}
        sx={{
          alignItems: 'center',
          backgroundColor: 'background.boxLight',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1,
          py: 2,
          height: '60px',
        }}
      >
        {group.tags.map((tag, tagIndex) => (
          <Stack
            key={tag}
            direction="row"
            sx={{
              gap: 1.5,
            }}
          >
            <TagItem tag={tag} />

            {!hideOpeartors && tagIndex < group.tags.length - 1 && <LogicalSeparator variant="OR" />}
          </Stack>
        ))}

        {isOverflowing && (
          <Stack
            sx={theme => ({
              position: 'absolute',
              right: 1,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(270deg, ${theme.palette.background.paper} 69.09%, ${alpha(theme.palette.background.paper, 0.1)} 100%)`,
              height: '58px',
              width: '120px',
            })}
          >
            <MoreTagItem />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
